import type TaxInfo from "../../domains/payment/taxes/TaxInfo";
import type DiscountInfo from "../../domains/payment/discounts/DiscountInfo";
import type CartPaymentInfo from "../../domains/payment/cart/CartPaymentInfo";
import type CartDiscountStrategy from "~/domains/payment/discounts/cart/cart/CartDiscountStrategy";
import CouponCartDiscountStrategy from "~/domains/payment/discounts/cart/cart/CouponCartDiscountStrategy";
import type ServiceDiscountStrategy from "~/domains/payment/discounts/cart/service/ServiceDiscountStrategy";
import type CartServiceInfo from "~/domains/payment/cart/CartServiceInfo";
import type ServiceTaxStrategy from "~/domains/payment/taxes/service/ServiceTaxStrategy";
import _ from "lodash";
import { isCartServiceInitialPayment, isCartServiceMonthly, isCartServiceSeasonal, isServicePackage, roundPrice } from "~/utils/payment";
import NdcFreeRoundServiceDiscountStrategy from "~/domains/payment/discounts/cart/service/NdcFreeRoundServiceDiscountStrategy";
import NdcFreeVisitServiceDiscountStrategy from "~/domains/payment/discounts/cart/service/NdcFreeVisitServiceDiscountStrategy";
import NdcFreeServiceServiceDiscountStrategy from "~/domains/payment/discounts/cart/service/NdcFreeServiceServiceDiscountStrategy";
import NdcReduceServiceDiscountStrategy from "~/domains/payment/discounts/cart/service/NdcReduceServiceDiscountStrategy";
import NdcUndefinedServiceDiscountStrategy from "~/domains/payment/discounts/cart/service/NdcUndefinedServiceDiscountStrategy";
import WebServiceDiscountStrategy from "~/domains/payment/discounts/cart/service/WebServiceDiscountStrategy";
import PrePayCartDiscountStrategy from "~/domains/payment/discounts/cart/cart/PrePayCartDiscountStrategy";
import SalesCartTaxStrategy from "~/domains/payment/taxes/cart/SalesCartTaxStrategy";
import type CartTaxStrategy from "~/domains/payment/taxes/cart/CartTaxStrategy";
import type OrderItemState from "~/domains/store/orderInfo/OrderItemState";

export class CartService {
  serviceDiscountStrategies: ServiceDiscountStrategy[];
  cartDiscountStrategies: CartDiscountStrategy[];
  serviceTaxStrategies: ServiceTaxStrategy[];
  cartTaxStrategies: CartTaxStrategy[];

  constructor() {
    this.serviceDiscountStrategies = [
      new NdcFreeRoundServiceDiscountStrategy(),
      new NdcFreeVisitServiceDiscountStrategy(),
      new NdcFreeServiceServiceDiscountStrategy(),
      new NdcReduceServiceDiscountStrategy(),
      new NdcUndefinedServiceDiscountStrategy(),
      new WebServiceDiscountStrategy(),
    ];
    this.cartDiscountStrategies = [
      new CouponCartDiscountStrategy(),
      // new CustomerCartDiscountStrategy(),
      new PrePayCartDiscountStrategy(),
    ];
    this.serviceTaxStrategies = [
      // new SalesServiceTaxStrategy(), // Deprecated for SalesCartTaxStrategy
    ];
    this.cartTaxStrategies = [
      new SalesCartTaxStrategy(),
    ];
  }

  public getCartPaymentInfo(cartInfo: CartPaymentInfo): CartPaymentInfo {
    const cartPaymentInfo: CartPaymentInfo = {
      services: cartInfo.services ?? [],
      coupons: cartInfo.coupons ?? [],
      ndc: cartInfo.ndc,
      franchiseDiscounts: cartInfo.franchiseDiscounts ?? [],
      taxesInfo: cartInfo.taxesInfo ?? [],
      payType: cartInfo.payType,
      installmentBillingMonthsRemaining: cartInfo.installmentBillingMonthsRemaining ?? 1,
      serviceMonthsRemaining: cartInfo.serviceMonthsRemaining ?? 1,
      packageInfo: cartInfo.packageInfo,
      orderInfo: cartInfo.orderInfo,
      subtotal: 0,
      monthlySubtotal: 0,
      initialPaymentSubtotal: 0,
      discounts: [],
      monthlyDiscounts: [],
      initialPaymentDiscounts: [],
      totalDiscount: 0,
      totalMonthlyDiscount: 0,
      totalInitialPaymentDiscount: 0,
      totalPrice: 0,
      totalMonthlyPrice: 0,
      totalInitialPaymentPrice: 0,
      taxes: [],
      monthlyTaxes: [],
      initialPaymentTaxes: [],
      totalTax: 0,
      totalMonthlyTax: 0,
      totalInitialPaymentTax: 0,
      totalDue: 0,
      totalMonthlyDue: 0,
      totalInitialPaymentDue: 0,
      dueToday: 0,
      dueMonthly: 0,
    };

    /* Calcualte services */
    cartPaymentInfo.services = cartPaymentInfo.services
      .map((cartService) => {
        const orderItem = cartPaymentInfo.orderInfo?.orderItems?.find((orderItem) => orderItem.uP_ServiceDefinitionId === cartService.ServiceDefId);
        const isPackageItem = orderItem?.isPackageItem ?? false;
        const service: CartServiceInfo = {
          ...cartService,
          isPackageItem: isPackageItem,
          subtotal: 0,
          monthlySubtotal: 0,
          initialPaymentSubtotal: 0,
          packageSubtotal: 0,
          discounts: [],
          monthlyDiscounts: [],
          initialPaymentDiscounts: [],
          packageDiscounts: [],
          totalDiscount: 0,
          totalMonthlyDiscount: 0,
          totalInitialPaymentDiscount: 0,
          totalPackageDiscount: 0,
          totalPrice: 0,
          totalMonthlyPrice: 0,
          totalInitialPaymentPrice: 0,
          totalPackagePrice: 0,
          taxes: [],
          monthlyTaxes: [],
          initialPaymentTaxes: [],
          packageTaxes: [],
          totalTax: 0,
          totalMonthlyTax: 0,
          totalInitialPaymentTax: 0,
          totalPackageTax: 0,
          totalDue: 0,
          totalMonthlyDue: 0,
          totalInitialPaymentDue: 0,
          totalPackageDue: 0,
        };

        /* Subtotal */
        service.subtotal = isCartServiceSeasonal(service) ? (service.remainingPrice ?? 0): 0;

        service.monthlySubtotal = isCartServiceMonthly(service) ? (service.mainPrice ?? 0) : 0;

        service.initialPaymentSubtotal = isCartServiceInitialPayment(service) ? (service.remainingPrice ?? 0): 0;

        service.packageSubtotal = isServicePackage(service) ? (orderItem?.extendedPrice ?? 0) : 0;

        /* Discounts */
        service.discounts = this.getServiceDiscounts(service, cartPaymentInfo);

        service.monthlyDiscounts = this.getServiceMonthlyDiscounts(service, cartPaymentInfo);

        service.initialPaymentDiscounts = this.getServiceInitialPaymentDiscounts(service, cartPaymentInfo);

        service.packageDiscounts = this.getServicePackageDiscounts(service, cartPaymentInfo, orderItem);

        /* Total Discount */
        service.totalDiscount = _.sum((service.discounts ?? []).map((discount) => discount.amount ?? 0));

        service.totalMonthlyDiscount = _.sum((service.monthlyDiscounts ?? []).map((discount) => discount.amount ?? 0));

        service.totalInitialPaymentDiscount = _.sum((service.initialPaymentDiscounts ?? []).map((discount) => discount.amount ?? 0));

        service.totalPackageDiscount = _.sum((service.packageDiscounts ?? []).map((discount) => discount.amount ?? 0));

        /* Total Price */
        service.totalPrice = Math.max(roundPrice((service.subtotal ?? 0) - (service.totalDiscount ?? 0), "nearest", 2), 0);

        service.totalMonthlyPrice = Math.max(roundPrice((service.monthlySubtotal ?? 0) - (service.totalMonthlyDiscount ?? 0), "nearest", 2), 0);

        service.totalInitialPaymentPrice = Math.max(roundPrice((service.initialPaymentSubtotal ?? 0) - (service.totalInitialPaymentDiscount ?? 0), "nearest", 2), 0);

        service.totalPackagePrice = Math.max(roundPrice((service.packageSubtotal ?? 0) - (service.totalPackageDiscount ?? 0), "nearest", 2), 0);

        /* Taxes */
        service.taxes = this.getServiceTaxes(service, cartPaymentInfo.taxesInfo);

        service.monthlyTaxes = this.getServiceMonthlyTaxes(service, cartPaymentInfo.taxesInfo);

        service.initialPaymentTaxes = this.getServiceInitialPaymentTaxes(service, cartPaymentInfo.taxesInfo);

        service.packageTaxes = []; // Taxes included, default to empty.

        /* Total Tax */
        service.totalTax = _.sum((service.taxes ?? []).map((tax) => tax.amount ?? 0));

        service.totalMonthlyTax = _.sum((service.monthlyTaxes ?? []).map((tax) => tax.amount ?? 0));

        service.totalInitialPaymentTax = _.sum((service.initialPaymentTaxes ?? []).map((tax) => tax.amount ?? 0));

        service.totalPackageTax = _.sum((service.packageTaxes ?? []).map((tax) => tax.amount ?? 0));

        /* Total Due */
        service.totalDue = (service.totalPrice ?? 0) + (service.totalTax ?? 0);

        service.totalMonthlyDue = (service.totalMonthlyPrice ?? 0) + (service.totalMonthlyTax ?? 0);

        service.totalInitialPaymentDue = (service.totalInitialPaymentPrice ?? 0) + (service.totalInitialPaymentTax ?? 0);

        service.totalPackageDue = (service.totalPackagePrice ?? 0) + (service.totalPackageTax ?? 0);

        /* Unit Price Sold */
        const freeRounds = service.discounts?.find(discount => ["ndcFreeRound", "ndcFreeService", "ndcFreeVisit"].includes(discount.type))?.freeRounds ?? 0;
        const adjustedRemainingRounds = _.max([(service.remainingRounds ?? 1) - freeRounds, 1]) ?? 1; // Clamp to 1 to avoid division by zero.

        if (isServicePackage(service)) {
          service.unitPriceSold = roundPrice(service.totalPackagePrice / adjustedRemainingRounds, "nearest", 2);
        } else if (isCartServiceMonthly(service)) {
          service.unitPriceSold = service.totalMonthlyPrice;
        } else if (isCartServiceInitialPayment(service)) {
          service.unitPriceSold = service.totalInitialPaymentPrice;
        } else {
          service.unitPriceSold = roundPrice(service.totalPrice / adjustedRemainingRounds, "nearest", 2);
        }

        /* Extended Price Sold */
        if (isServicePackage(service)) {
          service.extendedPriceSold = service.totalPackagePrice;
        } else if (isCartServiceMonthly(service)) {
          service.extendedPriceSold = roundPrice(service.totalMonthlyPrice * cartPaymentInfo.serviceMonthsRemaining, "nearest", 2);
        } else if (isCartServiceInitialPayment(service)) {
          service.extendedPriceSold = service.totalInitialPaymentPrice;
        } else {
          service.extendedPriceSold = service.totalPrice;
        }

        return service;
      });

    /* Calculate cart */
    /* Subtotals */
    cartPaymentInfo.subtotal = _.sum(
      cartPaymentInfo.services
      .map((service) => service.subtotal ?? 0));

    cartPaymentInfo.monthlySubtotal = _.sum(
      cartPaymentInfo.services
      .map((service) => service.monthlySubtotal ?? 0));

    cartPaymentInfo.initialPaymentSubtotal = _.sum(
      cartPaymentInfo.services
      .map((service) => service.initialPaymentSubtotal ?? 0));

    cartPaymentInfo.packageSubtotal = _.sum(
      cartPaymentInfo.services
      .map((service) => service.packageSubtotal ?? 0)
    );

    /* Discounts */
    const serviceDiscounts = cartPaymentInfo.services.flatMap((service) => service.discounts ?? []);
    cartPaymentInfo.discounts = this.getOrderDiscounts(cartPaymentInfo, serviceDiscounts);

    const serviceMonthlyDiscounts = cartPaymentInfo.services.flatMap((service) => service.monthlyDiscounts ?? []);
    cartPaymentInfo.monthlyDiscounts = this.getOrderMonthlyDiscounts(cartPaymentInfo, serviceMonthlyDiscounts);

    const serviceInitialPaymentDiscounts = cartPaymentInfo.services.flatMap((service) => service.initialPaymentDiscounts ?? []);
    cartPaymentInfo.initialPaymentDiscounts = this.getOrderInitialPaymentDiscounts(cartPaymentInfo, serviceInitialPaymentDiscounts);

    const servicePackageDiscounts = cartPaymentInfo.services.flatMap((service) => service.packageDiscounts ?? []);
    cartPaymentInfo.packageDiscounts = servicePackageDiscounts;

    /* Total Discount */
    cartPaymentInfo.totalDiscount = _.sum(cartPaymentInfo.discounts.map((discount) => discount.amount ?? 0));

    cartPaymentInfo.totalMonthlyDiscount = _.sum(cartPaymentInfo.monthlyDiscounts.map((discount) => discount.amount ?? 0));

    cartPaymentInfo.totalInitialPaymentDiscount = _.sum(cartPaymentInfo.initialPaymentDiscounts.map((discount) => discount.amount ?? 0));

    cartPaymentInfo.totalPackageDiscount = _.sum(cartPaymentInfo.packageDiscounts.map((discount) => discount.amount ?? 0));

    /* Total Price */
    cartPaymentInfo.totalPrice = Math.max(roundPrice((cartPaymentInfo.subtotal ?? 0) - (cartPaymentInfo.totalDiscount ?? 0), "nearest", 2), 0);

    cartPaymentInfo.totalMonthlyPrice = Math.max(roundPrice((cartPaymentInfo.monthlySubtotal ?? 0) - (cartPaymentInfo.totalMonthlyDiscount ?? 0), "nearest", 2), 0);

    cartPaymentInfo.totalInitialPaymentPrice = Math.max(roundPrice((cartPaymentInfo.initialPaymentSubtotal ?? 0) - (cartPaymentInfo.totalInitialPaymentDiscount ?? 0), "nearest", 2), 0);

    cartPaymentInfo.totalPackagePrice = Math.max(roundPrice((cartPaymentInfo.packageSubtotal ?? 0) - (cartPaymentInfo.totalPackageDiscount ?? 0), "nearest", 2), 0);

    /* Taxes */
    cartPaymentInfo.taxes = this.getCartTaxes(cartPaymentInfo);

    cartPaymentInfo.monthlyTaxes = this.getCartMonthlyTaxes(cartPaymentInfo);

    cartPaymentInfo.initialPaymentTaxes = this.getCartInitialPaymentTaxes(cartPaymentInfo);

    cartPaymentInfo.packageTaxes = []; // Taxes included in order.orderItems or only in package.price?

    /* Total Tax */
    cartPaymentInfo.totalTax = _.sum(cartPaymentInfo.taxes.map((tax) => tax.amount ?? 0));

    cartPaymentInfo.totalMonthlyTax = _.sum(cartPaymentInfo.monthlyTaxes?.map((tax) => tax.amount ?? 0));

    cartPaymentInfo.totalInitialPaymentTax = _.sum(cartPaymentInfo.initialPaymentTaxes?.map((tax) => tax.amount ?? 0));

    cartPaymentInfo.totalPackageTax = _.sum(cartPaymentInfo.packageTaxes?.map((tax) => tax.amount ?? 0));

    /* Total Due */
    cartPaymentInfo.totalDue = (cartPaymentInfo.totalPrice ?? 0) + (cartPaymentInfo.totalTax ?? 0);

    cartPaymentInfo.totalMonthlyDue = (cartPaymentInfo.totalMonthlyPrice ?? 0) + (cartPaymentInfo.totalMonthlyTax ?? 0);

    cartPaymentInfo.totalInitialPaymentDue = (cartPaymentInfo.totalInitialPaymentPrice ?? 0) + (cartPaymentInfo.totalInitialPaymentTax ?? 0);

    cartPaymentInfo.totalPackageDue = (cartPaymentInfo.totalPackagePrice ?? 0) + (cartPaymentInfo.totalPackageTax ?? 0);

    switch (cartPaymentInfo.payType) {
      case "autoPayInstallment":
        cartPaymentInfo.dueToday = cartPaymentInfo.totalInitialPaymentDue;
        cartPaymentInfo.dueMonthly = roundPrice(
          (cartPaymentInfo.totalMonthlyDue ?? 0) +
          (((cartPaymentInfo.totalDue ?? 0) + (cartPaymentInfo.totalPackageDue ?? 0))/ cartPaymentInfo.installmentBillingMonthsRemaining),
          "nearest",
          2);
        break;
      case "autoPay":
      case "payAfterEach":
        cartPaymentInfo.dueToday = cartPaymentInfo.totalInitialPaymentDue;
        cartPaymentInfo.dueMonthly = cartPaymentInfo.totalMonthlyDue ?? 0;
        break;
      case "payNow":
        cartPaymentInfo.dueToday = cartPaymentInfo.totalDue + cartPaymentInfo.totalInitialPaymentDue;
        cartPaymentInfo.dueMonthly = cartPaymentInfo.totalMonthlyDue ?? 0;
        break;
    }

    return cartPaymentInfo;
  }

  private getServiceDiscounts(cartServiceInfo: CartServiceInfo, cartPaymentInfo: CartPaymentInfo): DiscountInfo[] {
    return this.serviceDiscountStrategies
      .map((serviceDiscountStrategy) => serviceDiscountStrategy.getDiscountInfo(cartServiceInfo, cartPaymentInfo))
      .filter((discountInfo) => discountInfo.amount ?? 0 > 0);
  }

  private getServiceMonthlyDiscounts(cartServiceInfo: CartServiceInfo, cartPaymentInfo: CartPaymentInfo): DiscountInfo[] {
    return this.serviceDiscountStrategies
      .map((serviceDiscountStrategy) => serviceDiscountStrategy.getMonthlyDiscountInfo(cartServiceInfo, cartPaymentInfo))
      .filter((discountInfo) => discountInfo.amount ?? 0 > 0);
  }

  private getServiceInitialPaymentDiscounts(cartServiceInfo: CartServiceInfo, cartPaymentInfo: CartPaymentInfo): DiscountInfo[] {
    return this.serviceDiscountStrategies
      .map((serviceDiscountStrategy) => serviceDiscountStrategy.getInitialPaymentDiscountInfo(cartServiceInfo, cartPaymentInfo))
      .filter((discountInfo) => discountInfo.amount ?? 0 > 0);
  }

  private getServicePackageDiscounts(cartServiceInfo: CartServiceInfo, cartPaymentInfo: CartPaymentInfo, orderItem?: OrderItemState): DiscountInfo[] {
    const packageDiscounts: DiscountInfo[] = [];

    if (isServicePackage(cartServiceInfo)) {
      packageDiscounts.unshift({
        type: "package",
        description: "Package Discount",
        value: 0,
        amount: roundPrice((orderItem?.extendedPrice ?? 0) - (orderItem?.extendedPriceSold ?? 0), "nearest", 2),
      });

      orderItem?.orderDiscounts?.forEach((orderDiscount) => {
        packageDiscounts.push({
          type: "coupon", // TODO: convert from orderItem.discountType?
          description: `${orderDiscount.couponCode} - ${orderDiscount.description}`,
          value: 0,
          amount: orderDiscount.amount,
        });
      });
    }

    return packageDiscounts.filter((discountInfo) => discountInfo.amount ?? 0 > 0);
  }

  private getServiceTaxes(cartServiceInfo: CartServiceInfo, taxes: TaxInfo[]): TaxInfo[] {
    return this.serviceTaxStrategies.flatMap((taxStrategy) => {
      const taxStrategyTaxes = taxes.filter((tax) => tax.type === taxStrategy.getTaxInfoType());

      return taxStrategyTaxes.map((tax) => taxStrategy.getTaxInfo(tax, cartServiceInfo));
    });
  }

  private getServiceMonthlyTaxes(cartServiceInfo: CartServiceInfo, taxes: TaxInfo[]): TaxInfo[] {
    return this.serviceTaxStrategies.flatMap((taxStrategy) => {
      const taxStrategyTaxes = taxes.filter((tax) => tax.type === taxStrategy.getTaxInfoType());

      return taxStrategyTaxes.map((tax) => taxStrategy.getMonthlyTaxInfo(tax, cartServiceInfo));
    });
  }

  private getServiceInitialPaymentTaxes(cartServiceInfo: CartServiceInfo, taxes: TaxInfo[]): TaxInfo[] {
    return this.serviceTaxStrategies.flatMap((taxStrategy) => {
      const taxStrategyTaxes = taxes.filter((tax) => tax.type === taxStrategy.getTaxInfoType());

      return taxStrategyTaxes.map((tax) => taxStrategy.getInitialPaymentTaxInfo(tax, cartServiceInfo));
    });
  }

  private getOrderDiscounts(cartPaymentInfo: CartPaymentInfo, existingDiscounts: DiscountInfo[]): DiscountInfo[] {
    this.cartDiscountStrategies.forEach((cartDiscountStrategy) => {
      const currentDiscountedSubtotal = Math.max((cartPaymentInfo.subtotal ?? 0) - _.sum(existingDiscounts.map((existingDiscount) => existingDiscount.amount ?? 0)), 0);

      const discount = cartDiscountStrategy.getDiscountInfo(cartPaymentInfo, currentDiscountedSubtotal);
      const discountAmount = discount.amount ?? 0;
      if (discountAmount > 0) {
        existingDiscounts.push(discount);
      }
    });

    return existingDiscounts;
  }

  private getOrderMonthlyDiscounts(cartPaymentInfo: CartPaymentInfo, existingMonthlyDiscounts: DiscountInfo[]): DiscountInfo[] {
    this.cartDiscountStrategies.forEach((cartDiscountStrategy) => {
      const currentDiscountedSubtotal = Math.max((cartPaymentInfo.monthlySubtotal ?? 0) - _.sum(existingMonthlyDiscounts.map((existingDiscount) => existingDiscount.amount ?? 0)), 0);

      const discount = cartDiscountStrategy.getMonthlyDiscountInfo(cartPaymentInfo, currentDiscountedSubtotal);
      const discountAmount = discount.amount ?? 0;
      if (discountAmount > 0) {
        existingMonthlyDiscounts.push(discount);
      }
    });

    return existingMonthlyDiscounts;
  }

  private getOrderInitialPaymentDiscounts(cartPaymentInfo: CartPaymentInfo, existingInitialPaymentDiscounts: DiscountInfo[]): DiscountInfo[] {
    this.cartDiscountStrategies.forEach((cartDiscountStrategy) => {
      const currentDiscountedSubtotal = Math.max((cartPaymentInfo.initialPaymentSubtotal ?? 0) - _.sum(existingInitialPaymentDiscounts.map((existingDiscount) => existingDiscount.amount ?? 0)), 0);

      const discount = cartDiscountStrategy.getInitialPaymentDiscountInfo(cartPaymentInfo, currentDiscountedSubtotal);
      const discountAmount = discount.amount ?? 0;
      if (discountAmount > 0) {
        existingInitialPaymentDiscounts.push(discount);
      }
    });

    return existingInitialPaymentDiscounts;
  }

  private getCartTaxes(cartPaymentInfo: CartPaymentInfo): TaxInfo[] {
    return this.cartTaxStrategies.flatMap((taxStrategy) => {
      const taxStrategyTaxes = cartPaymentInfo.taxesInfo.filter((tax) => tax.type === taxStrategy.getTaxInfoType());

      return taxStrategyTaxes.map((tax) => taxStrategy.getTaxInfo(tax, cartPaymentInfo));
    });
  }

  private getCartMonthlyTaxes(cartPaymentInfo: CartPaymentInfo): TaxInfo[] {
    return this.cartTaxStrategies.flatMap((taxStrategy) => {
      const taxStrategyTaxes = cartPaymentInfo.taxesInfo.filter((tax) => tax.type === taxStrategy.getTaxInfoType());

      return taxStrategyTaxes.map((tax) => taxStrategy.getMonthlyTaxInfo(tax, cartPaymentInfo));
    });
  }

  private getCartInitialPaymentTaxes(cartPaymentInfo: CartPaymentInfo): TaxInfo[] {
    return this.cartTaxStrategies.flatMap((taxStrategy) => {
      const taxStrategyTaxes = cartPaymentInfo.taxesInfo.filter((tax) => tax.type === taxStrategy.getTaxInfoType());

      return taxStrategyTaxes.map((tax) => taxStrategy.getInitialPaymentTaxInfo(tax, cartPaymentInfo));
    });
  }
}
